/**
 * One email field and one button that sends a sign-in link: for a customer who needs a new link,
 * and during onboarding, where the email is attached to the visitor's session. The browser checks
 * the address first (the button submits the form), every server answer is said in words tied to
 * the field, and a successful send hands the address to the page.
 */
import * as stylex from "@stylexjs/stylex";
import { useEffect, useState } from "react";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import { Button } from "@lattice/ui/Button";
import { Field } from "@lattice/ui/Field";
import { Input } from "@lattice/ui/Input";
import type { EmailOutcome } from "./api.ts";
import { turnstileToken } from "./turnstile.ts";
const EMAIL_ID = "link-email";
type Problem = Exclude<EmailOutcome, "sent"> | "offline" | "already_sent" | null;
const MESSAGES: Readonly<Record<Exclude<Problem, null>, string>> = {
  invalid: "That email address looks incomplete. Check it and try again.",
  too_many: "You've asked five times in the last hour. Use the newest email, or try again later.",
  failed: "We couldn't send the email. Try again in a moment.",
  offline: "You're offline, so nothing was sent. Reconnect, then try again.",
  already_sent: "We already sent your link to this address. Check your spam folder, or enter a different one."
};

/* A failed request says nothing about why, and waiting a moment does not help when offline. */
function failure(): Problem {
  return navigator.onLine ? "failed" : "offline";
}
function describedByIds(own: string | undefined, fieldIds: string | undefined): string | undefined {
  const ids = [own, fieldIds].filter(id => id !== undefined);
  return ids.length > 0 ? ids.join(" ") : undefined;
}

/* The Worker trims and lowercases an address before storing it, so this compares the same way. */
function sameAddress(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}
export function EmailLinkForm({
  submitLabel,
  onSent,
  request,
  initialEmail = "",
  focusOnMount = false,
  alreadySentTo,
  describedBy
}: {
  readonly submitLabel: string;
  readonly onSent: (email: string) => void;
  /** The call that sends the link: a new sign-in link, or the onboarding email capture. */
  readonly request: (email: string, turnstileToken: string) => Promise<EmailOutcome>;
  /** Filled in when the visitor comes back to correct an address they already sent to. */
  readonly initialEmail?: string;
  /** Focuses the field and selects its text, for a form the visitor opened to correct an address. */
  readonly focusOnMount?: boolean;
  /**
   * The address a link already went to. Sending to it again is refused with a hint, because it
   * would spend one of the five links allowed an hour and the person likely wants another address.
   */
  readonly alreadySentTo?: string;
  /** The id of text that explains the field, read with it when focus lands there. */
  readonly describedBy?: string;
}) {
  const [email, setEmail] = useState(initialEmail);
  const [sending, setSending] = useState(false);
  const [problem, setProblem] = useState<Problem>(null);

  /*
    An email input throws on setSelectionRange, so the whole value is selected with select(), which
    scrolls a long address to its end. Scrolling back keeps its start, the part people check, in view.
  */
  const selectField = () => {
    const input = document.getElementById(EMAIL_ID);
    if (!(input instanceof HTMLInputElement)) return;
    input.focus();
    input.select();
    input.scrollLeft = 0;
  };
  useEffect(() => {
    if (focusOnMount) selectField();
  }, [focusOnMount]);
  const send = async () => {
    /* A repeated refusal re-renders nothing, so moving to the field is what shows the press landed. */
    if (alreadySentTo !== undefined && sameAddress(email, alreadySentTo)) {
      setProblem("already_sent");
      selectField();
      return;
    }

    /* Checked before Turnstile, whose widget retries on its own and can keep an offline send waiting. */
    if (!navigator.onLine) {
      setProblem("offline");
      return;
    }
    setSending(true);
    setProblem(null);
    try {
      const outcome = await request(email, await turnstileToken());
      if (outcome === "sent") onSent(email);else setProblem(outcome === "failed" ? failure() : outcome);
    } catch {
      setProblem(failure());
    } finally {
      setSending(false);
    }
  };
  return <form noValidate className="x78zum5 xdt5ytf x1r0x45p xh8yej3" onSubmit={event => {
    event.preventDefault();
    if (!sending) void send();
  }}>
      <Field label="Email" id={EMAIL_ID} error={problem === null ? null : MESSAGES[problem]}>
        {control => <Input {...control} type="email" size="lg" autoComplete="email" value={email} aria-describedby={describedByIds(describedBy, control["aria-describedby"])} onChange={value => {
        setEmail(value);

        /* These two describe the address that was typed, so they no longer apply once it changes. */
        if (problem === "invalid" || problem === "already_sent") setProblem(null);
      }} />}
      </Field>
      <Button type="submit" testId="send-link" busy={sending} wide>
        {sending ? "Sending…" : submitLabel}
      </Button>
    </form>;
}